import React, { useState, useEffect, useMemo, useCallback, memo } from "react";
import { customizationAssetApi, CustomizationAsset } from "../../services/customizationAssetApi";
import { getImageUrl } from "../../utils/imageUtils";
import { useApi } from "../../context/ApiContext";
import { useStore } from "../../context/StoreContext";
import { FaCheck, FaSearch, FaFont } from "react-icons/fa";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { Spinner } from "../ui/Spinner";
import { MultiSelect } from "../ui/MultiSelect";

interface AssetSelectorProps {
  selectedAssetIds: string[];
  onSelectionChange: (assetIds: string[]) => void;
  selectedFonts?: string[];
  onFontsChange?: (fonts: string[]) => void;
  types?: ("background" | "shape")[];
  showFonts?: boolean; // Whether to show fonts tab
}

// Helper function to get all available fonts
const getAllAvailableFonts = (): Array<{ value: string; label: string }> => {
  const systemFonts = [
    "Arial", "Helvetica", "Times New Roman", "Courier New", "Verdana", 
    "Georgia", "Palatino", "Garamond", "Comic Sans MS", "Trebuchet MS", 
    "Impact", "Lucida Console", "Tahoma"
  ];
  
  const typefaceFonts = [
    "Almarai", "Anton", "Barlow Condensed", "BBH Bartle", "Cairo", "Caveat",
    "Dancing Script", "Edu SA Beginner", "Follow", "Great Vibes", "Josefin Sans Thin",
    "Lobster", "Lobster Two", "Noto Sans Symbols", "Orbitron", "Pacifico",
    "Satisfy", "Shadows Into Light", "Smooch Sans Thin", "Space Grotesk Light", "Tajawal"
  ];
  
  const googleFonts = [
    "Inter", "Roboto", "Open Sans", "Lato", "Montserrat", "Oswald", "Raleway",
    "Poppins", "Source Sans Pro", "Playfair Display", "Merriweather", "Ubuntu",
    "Dancing Script", "Pacifico", "Lobster", "Bebas Neue", "Bangers", "Righteous",
    "Fredoka One", "Comfortaa", "Quicksand", "Caveat", "Kalam", "Permanent Marker",
    "Shadows Into Light", "Indie Flower", "Crimson Text", "Fira Sans", "PT Sans",
    "Nunito", "Work Sans", "Rubik", "Noto Sans", "Mukta", "DM Sans", "Manrope",
    "Outfit", "Plus Jakarta Sans", "Trirong",
    // Arabic Fonts
    "Amiri", "Changa", "El Messiri", "Harmattan", "Lateef", "Markazi Text",
    "Reem Kufi", "Scheherazade New", "Noto Sans Arabic", "Noto Naskh Arabic",
    "Noto Kufi Arabic", "IBM Plex Sans Arabic", "Cairo", "Tajawal", "Almarai"
  ];
  
  const allFontsSet = new Set([...systemFonts, ...typefaceFonts, ...googleFonts]);
  const allFonts = Array.from(allFontsSet);
  
  return allFonts.map(font => ({ value: font, label: font })).sort((a, b) => 
    a.label.localeCompare(b.label)
  );
};

export const AssetSelector: React.FC<AssetSelectorProps> = ({
  selectedAssetIds,
  onSelectionChange,
  selectedFonts = [],
  onFontsChange,
  types = ["background", "shape"],
  showFonts = false,
}) => {
  const { api } = useApi();
  const { store } = useStore();
  const [assets, setAssets] = useState<CustomizationAsset[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [filterType, setFilterType] = useState<"all" | "background" | "shape" | "fonts">("all");

  // Filter available fonts based on product-level or store-level restrictions
  // Priority: product-level > store-level > all fonts
  const availableFontOptions = useMemo(() => {
    const allFonts = getAllAvailableFonts();
    
    // If product has fonts selected, show all fonts (user can select from all)
    // But if product has no fonts selected, limit to store-level fonts if they exist
    if (selectedFonts.length === 0 && store?.allowedDesignerFonts && store.allowedDesignerFonts.length > 0) {
      // Product has no fonts selected, so limit to store-level fonts
      const storeFontsLower = store.allowedDesignerFonts.map(f => f.toLowerCase());
      return allFonts.filter(font => 
        storeFontsLower.includes(font.value.toLowerCase())
      );
    }
    
    // Product has fonts selected OR no store-level restrictions, show all fonts
    return allFonts;
  }, [selectedFonts, store?.allowedDesignerFonts]);

  useEffect(() => {
    if (showModal && filterType !== "fonts") {
      const fetchAssets = async () => {
        setLoading(true);
        setError(null);
        try {
          const allAssets = await customizationAssetApi.getAssets();
          // Filter by types if specified
          const filtered = types.length > 0
            ? allAssets.filter((asset) => types.includes(asset.type))
            : allAssets;
          setAssets(filtered);
        } catch (err: any) {
          setError(err.response?.data?.error || "Failed to load assets");
        } finally {
          setLoading(false);
        }
      };

      fetchAssets();
    } else if (showModal && filterType === "fonts") {
      setLoading(false);
    }
  }, [showModal, types, filterType]);

  const filteredAssets = useMemo(() => {
    let filtered = assets;
    
    // Filter by type
    if (filterType !== "all") {
      filtered = filtered.filter((asset) => asset.type === filterType);
    }
    
    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (asset) =>
          asset.name.toLowerCase().includes(query) ||
          asset.category?.toLowerCase().includes(query) ||
          asset.type.toLowerCase().includes(query)
      );
    }
    
    return filtered;
  }, [assets, searchQuery, filterType]);

  const groupedAssets = useMemo(() => {
    const grouped: Record<string, CustomizationAsset[]> = {};
    filteredAssets.forEach((asset) => {
      const key = `${asset.type}-${asset.category || "Uncategorized"}`;
      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(asset);
    });
    return grouped;
  }, [filteredAssets]);

  const toggleAsset = useCallback((assetId: string) => {
    const isSelected = selectedAssetIds.includes(assetId);
    if (isSelected) {
      onSelectionChange(selectedAssetIds.filter((id) => id !== assetId));
    } else {
      onSelectionChange([...selectedAssetIds, assetId]);
    }
  }, [selectedAssetIds, onSelectionChange]);

  const handleFilterChange = useCallback((type: "all" | "background" | "shape" | "fonts") => {
    setFilterType(type);
  }, []);

  const selectedCount = selectedAssetIds.length;
  const selectedFontsCount = selectedFonts.length;
  const backgroundsCount = useMemo(() => assets.filter((a) => a.type === "background").length, [assets]);
  const shapesCount = useMemo(() => assets.filter((a) => a.type === "shape").length, [assets]);

  return (
    <>
      <div className="space-y-2">
        <Button
          type="button"
          variant="outline"
          onClick={() => setShowModal(true)}
          className="w-full"
        >
          {selectedCount > 0 || selectedFontsCount > 0
            ? `Select Assets (${selectedCount} asset${selectedCount !== 1 ? "s" : ""}${showFonts && selectedFontsCount > 0 ? `, ${selectedFontsCount} font${selectedFontsCount !== 1 ? "s" : ""}` : ""} selected)`
            : "Select Assets"}
        </Button>
        {(selectedCount > 0 || (showFonts && selectedFontsCount > 0)) && (
          <p className="text-xs text-gray-500">
            {selectedCount > 0 && `${selectedCount} asset${selectedCount !== 1 ? "s" : ""}`}
            {selectedCount > 0 && showFonts && selectedFontsCount > 0 && ", "}
            {showFonts && selectedFontsCount > 0 && `${selectedFontsCount} font${selectedFontsCount !== 1 ? "s" : ""}`}
            {" selected"}
          </p>
        )}
      </div>

      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title="Select Customization Assets"
        size="xl"
        footer={
          <div className="flex items-center justify-between w-full">
            <div className="text-sm text-gray-600">
              {filterType === "fonts" 
                ? `${selectedFontsCount} font${selectedFontsCount !== 1 ? "s" : ""} selected`
                : `${selectedCount} asset${selectedCount !== 1 ? "s" : ""} selected`}
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setShowModal(false)}>
                Done
              </Button>
            </div>
          </div>
        }
      >
        <div className="space-y-4 min-h-[500px] flex flex-col">
          {/* Search and Filter */}
          <div className="space-y-3 flex-shrink-0">
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search assets by name, category, or type..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Filter Tabs */}
            <div className="flex gap-2 border-b" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleFilterChange("all");
                }}
                className={`px-4 py-2 text-sm font-medium ${
                  filterType === "all"
                    ? "text-blue-600 border-b-2 border-blue-600"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                All ({assets.length})
              </button>
              {types.includes("background") && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleFilterChange("background");
                  }}
                  className={`px-4 py-2 text-sm font-medium ${
                    filterType === "background"
                      ? "text-blue-600 border-b-2 border-blue-600"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  Backgrounds ({backgroundsCount})
                </button>
              )}
              {types.includes("shape") && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleFilterChange("shape");
                  }}
                  className={`px-4 py-2 text-sm font-medium ${
                    filterType === "shape"
                      ? "text-blue-600 border-b-2 border-blue-600"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  Shapes ({shapesCount})
                </button>
              )}
              {showFonts && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleFilterChange("fonts");
                  }}
                  className={`px-4 py-2 text-sm font-medium flex items-center gap-2 ${
                    filterType === "fonts"
                      ? "text-blue-600 border-b-2 border-blue-600"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  <FaFont className="w-3 h-3" />
                  Fonts ({selectedFontsCount > 0 ? selectedFontsCount : "All"})
                </button>
              )}
            </div>
          </div>

          {/* Assets Grid or Fonts Selector */}
          <div className="flex-1 min-h-0">
            {filterType === "fonts" ? (
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Select which fonts users can use in the 2D designer for this product. Leave empty to allow all fonts (or use store-level settings).
                  </p>
                  {onFontsChange && (
                    <MultiSelect
                      label="Select Allowed Fonts"
                      options={availableFontOptions}
                      value={selectedFonts}
                      onChange={onFontsChange}
                      placeholder="Select fonts to allow (leave empty to allow all fonts)"
                      helperText={
                        selectedFonts.length === 0 && store?.allowedDesignerFonts && store.allowedDesignerFonts.length > 0
                          ? `If no fonts are selected, store-level fonts (${store.allowedDesignerFonts.length} font${store.allowedDesignerFonts.length !== 1 ? "s" : ""}) will be used.`
                          : "If no fonts are selected, all fonts will be available to users in the 2D designer."
                      }
                      isFontSelector={true}
                    />
                  )}
                  {selectedFonts.length > 0 && (
                    <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                      {selectedFonts.length} font{selectedFonts.length !== 1 ? "s" : ""} selected. Users will only be able to use these fonts in the 2D designer for this product.
                    </p>
                  )}
                </div>
              </div>
            ) : loading ? (
              <div className="flex justify-center py-12">
                <Spinner size="lg" />
              </div>
            ) : error ? (
              <div className="text-center py-8">
                <p className="text-red-500">{error}</p>
              </div>
            ) : Object.keys(groupedAssets).length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <p>No assets found. Upload assets in the Customization Assets page.</p>
              </div>
            ) : (
              <div className="space-y-6 h-full overflow-y-auto pr-2">
                {Object.entries(groupedAssets).map(([key, groupAssets]) => {
                  const [type, category] = key.split("-");
                  return (
                    <div key={key} className="space-y-2">
                      <h4 className="text-sm font-semibold text-gray-700 capitalize sticky top-0 bg-white py-1 z-10">
                        {type} - {category}
                      </h4>
                      <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
                        {groupAssets.map((asset) => (
                          <AssetItem
                            key={asset._id}
                            asset={asset}
                            isSelected={selectedAssetIds.includes(asset._id)}
                            onToggle={toggleAsset}
                            baseURL={api.baseURL}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </Modal>
    </>
  );
};

// Memoized asset item component to prevent unnecessary re-renders
interface AssetItemProps {
  asset: CustomizationAsset;
  isSelected: boolean;
  onToggle: (assetId: string) => void;
  baseURL: string;
}

const AssetItem = memo<AssetItemProps>(({ asset, isSelected, onToggle, baseURL }) => {
  return (
    <div
      className={`relative border-2 rounded overflow-hidden cursor-pointer transition-all group ${
        isSelected
          ? "border-blue-500 shadow-md ring-2 ring-blue-200"
          : "border-gray-200 hover:border-gray-400"
      }`}
      onClick={(e) => {
        e.stopPropagation();
        onToggle(asset._id);
      }}
      title={asset.name}
    >
      <div className="aspect-square bg-gray-100 flex items-center justify-center">
        {asset.url ? (
          <img
            src={getImageUrl(asset.url, baseURL)}
            alt={asset.name}
            className="w-full h-full object-cover"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = "none";
            }}
          />
        ) : (
          <div className="text-gray-400 text-xs text-center p-1">
            {asset.name}
          </div>
        )}
      </div>
      {isSelected && (
        <div className="absolute top-0.5 right-0.5 bg-blue-500 text-white rounded-full p-0.5">
          <FaCheck className="text-[10px]" />
        </div>
      )}
      <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-70 text-white text-[10px] px-1 py-0.5 truncate opacity-0 group-hover:opacity-100 transition-opacity">
        {asset.name}
      </div>
    </div>
  );
});

AssetItem.displayName = "AssetItem";

